import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Collapse } from 'reactstrap';
import useJuryStore from '../../../store/juryStore';
import styles from './JuryCategoryFilter.module.scss';
import JuryCategoryBadge from '../JuryCategoryBadge/JuryCategoryBadge';

const JuryCategoryFilter = ({ selectedBadges, onChange }) => {
    const badges = useJuryStore((s) => s.badges);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const groupedBadges = useMemo(() => {
        const groups = {};
        badges.forEach((badge) => {
            if (!groups[badge.group]) groups[badge.group] = [];
            groups[badge.group].push(badge);
        });

        Object.values(groups).forEach((groupBadges) => {
            groupBadges.sort((a, b) => a.value.localeCompare(b.value));
        });

        return groups;
    }, [badges]);

    const toggle = () => setIsOpen(!isOpen);

    const handleToggleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    };

    const handleContainerKeyDown = (e) => {
        if (e.key === 'Escape' && isOpen) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen]);

    const handleBadgeClick = (group, value) => {
        const nextBadges = { ...selectedBadges };
        if (value === 'all') {
            delete nextBadges[group];
        } else {
            nextBadges[group] = value;
        }
        onChange(nextBadges);
    };

    const displayValue = useMemo(() => {
        const selected = Object.values(selectedBadges).filter((v) => v && v !== 'all');
        return selected.length > 0 ? selected.join(', ') : 'Alle';
    }, [selectedBadges]);

    return (
        <div className={styles.CategoryFilter} ref={containerRef} onKeyDown={handleContainerKeyDown}>
            <div
                id="category-filter"
                className={`${styles.SelectToggle} ${isOpen ? styles.isOpen : ''}`}
                onClick={toggle}
                onKeyDown={handleToggleKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                aria-haspopup="true"
                aria-labelledby="category-filter-label"
                aria-label={`Kategorie Filter, aktuell ausgewählt: ${displayValue}`}
            >
                <span className={styles.SelectedValue} aria-hidden="true">
                    {displayValue}
                </span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    width={16}
                    height={12}
                    className={`${styles.Chevron} ${isOpen ? styles.rotated : ''}`}
                    aria-hidden="true"
                >
                    <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2 5l6 6 6-6"
                    />
                </svg>
            </div>

            <Collapse isOpen={isOpen} className={styles.CollapseWrapper}>
                <div className={styles.DropdownContent}>
                    {Object.entries(groupedBadges)
                        .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                        .map(([group, groupBadges]) => (
                            <fieldset key={group} className={styles.Group} aria-labelledby={`group-header-${group}`}>
                                <legend id={`group-header-${group}`} className={styles.GroupHeader}>
                                    {group}
                                </legend>
                                <div className={styles.PillContainer}>
                                    <JuryCategoryBadge
                                        label="Alle"
                                        color={styles.badgeAll}
                                        isSelected={!selectedBadges[group] || selectedBadges[group] === 'all'}
                                        onClick={() => handleBadgeClick(group, 'all')}
                                    />
                                    {groupBadges.map((badge) => (
                                        <JuryCategoryBadge
                                            key={badge._id}
                                            label={badge.value}
                                            color={badge.color}
                                            isSelected={selectedBadges[group] === badge.value}
                                            onClick={() => handleBadgeClick(group, badge.value)}
                                        />
                                    ))}
                                </div>
                            </fieldset>
                        ))}
                </div>
            </Collapse>
        </div>
    );
};

export default JuryCategoryFilter;
